import { HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import { APP_INITIALIZER, Injector, ModuleWithProviders, NgModule, Optional, SkipSelf } from "@angular/core";

import { ApiConfiguration } from "../api-configuration";
import { MOCKUP, SelfServiceMockupInterceptor } from "../interceptors/self-service.interceptor";
import { MOCKUP_DATA } from "../interceptors/self-service.mockup";

/**
 * The Self-Service configuration object
 */
export interface SelfServiceConfig {
  /**
   * The base URL used for interacting with backend resources. Default is `/`.
   */
  baseUrl?: string;
  /**
   * Whether requests should be intercepted with mockup data or not. Default is `false`.
   */
  mockup?: boolean;
}

/**
 * Sets the `ApiConfiguration` root URL from the provided base URL, or from the base URL specified in the elements environment configuration object
 */
export function setBaseUrl(baseUrl?: string): (injector: Injector, apiConfiguration: ApiConfiguration) => () => void {
  const callback = (injector: Injector, apiConfiguration: ApiConfiguration) => () => {
    // TODO (stefan): Getting the injection token from string until we have the @vattenfall/core library setup
    // After that we should provide the actual token as a dependency to this provider and get it from there
    const elementsConfiguration = injector.get("ELEMENTS_ENVIRONMENT", {});
    apiConfiguration.rootUrl = baseUrl || elementsConfiguration?.endpoints?.selfServiceApi || "";
  };
  return callback;
}

/**
 * `SelfServiceModule` manages configurations used when communicating with the Self-Service API, and should be imported once at the application root.
 *
 * To create module use the `forRoot` static method, it will create a module that contain all the configurations and instantiation logic required to communicate with the Self-Service API.
 *
 * The module should be imported as follows: `imports: [ SelfServiceModule.forRoot({ baseUrl: "https://some-host.com" }) ]` in your application's root module. If no base URL has been provided, self-service module will try to read the base URL from the elements environment configuration object.
 *
 * Optionally, requests can be intercepted and return mocked data as response. To enable this feature, set the `mockup` option to `true` when calling the `forRoot` method. Mockup data needs to be added to a mockup file in the `@vattenfall/self-service` root directory. Check the package documentation for more information.
 */
@NgModule({})
export class SelfServiceModule {
  static forRoot(selfServiceConfig?: SelfServiceConfig): ModuleWithProviders<SelfServiceModule> {
    return {
      ngModule: SelfServiceModule,
      providers: [
        // TODO: The mockups are currently not being tree-shaken.
        // Check solution using dynamic imports and factories instead to load MOCKUP_DATA and interceptor instead.
        // Provide mockup data
        selfServiceConfig?.mockup
          ? {
              provide: MOCKUP,
              useValue: MOCKUP_DATA,
            }
          : [],
        // Provide mockup interceptor
        selfServiceConfig?.mockup
          ? {
              provide: HTTP_INTERCEPTORS,
              useClass: SelfServiceMockupInterceptor,
              multi: true,
            }
          : [],
        // Provide root URL for the base service
        {
          provide: APP_INITIALIZER,
          useFactory: setBaseUrl(selfServiceConfig?.baseUrl),
          deps: [Injector, ApiConfiguration],
          multi: true,
        },
      ],
    };
  }

  constructor(
    @Optional()
    @SkipSelf()
    private parentModule: SelfServiceModule,
    @Optional()
    private http: HttpClient
  ) {
    if (this.parentModule) {
      throw new Error(
        "Could not import 'SelfServiceModule'. This module has already been imported, please make sure it is imported once and in the application root module."
      );
    }
    if (!this.http) {
      throw new Error(
        "Could not import 'SelfServiceModule'. You need to import the HttpClientModule in the application root module."
      );
    }
  }
}
